import cx from "clsx"
import type { TemplateModule } from "shared"
import { DragPreview } from "../../../dragAndDrop/DragPreview"
import { useSortableItem } from "../../../dragAndDrop/useSortableItem"
import sortable from "../../../dragAndDrop/sortable.module.css"
import { isModule, placedModule, placedModuleZone } from "../utils/templateDrag"
import ArrowDownIcon from "../../../assets/icon-arrow-down.svg?react"
import ArrowUpIcon from "../../../assets/icon-arrow-up.svg?react"
import LayersIcon from "../../../assets/icon-layers.svg?react"
import TrashIcon from "@rawa/ui/assets/icon-trash.svg?react"
import { Tag } from "@rawa/ui/Tag"
import styles from "./PlacedModuleCard.module.css"

/**
 * A module on a template's canvas, with the controls that move it and take it
 * off. It can also be dragged, as a copy of itself that follows the pointer,
 * and another module, placed or from the library, dropped on its top or bottom
 * edge; the screen decides what a drop does (see eventForDrop), and the
 * buttons stay as the way to do the same from a keyboard.
 */
export function PlacedModuleCard({
  module,
  first,
  last,
  onMove,
  onRemove,
}: {
  module: TemplateModule
  first: boolean
  last: boolean
  onMove: (by: -1 | 1) => void
  onRemove: () => void
}) {
  const item = useSortableItem<HTMLLIElement>({
    id: module.id,
    kind: placedModule,
    zone: placedModuleZone,
    accepts: isModule,
  })

  const card = (
    <>
      <LayersIcon aria-hidden="true" />
      <span className={styles.moduleText}>
        <strong>{module.name}</strong>
        <small>{module.entityLinkLabel}</small>
      </span>
      {module.archived && <Tag colour="disabled">Archived</Tag>}
      <button
        type="button"
        className={styles.rowAction}
        aria-label={`Move ${module.name} up`}
        disabled={first}
        onClick={() => onMove(-1)}
      >
        <ArrowUpIcon aria-hidden="true" />
      </button>
      <button
        type="button"
        className={styles.rowAction}
        aria-label={`Move ${module.name} down`}
        disabled={last}
        onClick={() => onMove(1)}
      >
        <ArrowDownIcon aria-hidden="true" />
      </button>
      <button
        type="button"
        className={styles.remove}
        aria-label={`Remove ${module.name}`}
        onClick={onRemove}
      >
        <TrashIcon aria-hidden="true" />
      </button>
    </>
  )

  return (
    <li
      ref={item.ref}
      className={cx(styles.module, sortable.item)}
      data-dragging={item.dragging || undefined}
      data-drop-edge={item.dropEdge ?? undefined}
    >
      {card}
      <DragPreview item={item} className={styles.module}>
        {card}
      </DragPreview>
    </li>
  )
}
